import { Easing, TransformBase } from "../../transforms/transform_base.js";
import { TransformStraightMove } from "../../transforms/transform_straight_move.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { TransformOpacity } from "../../transforms/transform_opacity.js";
import { TransformRotate } from "../../transforms/transform_rotate.js";
import { DOM_ANIMATIONS_SUPPORTED, DOM_LINEAR_EASING_SUPPORTED, domScaleCss, stampDomAnimationStart } from "./dom_backend.js";
import { Vector2 } from "../../common/vector2.js";
import type { Transformable } from "../../transforms/transformable.js";

type DomAnimatedProperty = "translate" | "scale" | "rotate" | "opacity";

export interface DomSample
{
  pos?: Vector2;
  scale?: Vector2;
  rotation?: number;
  opacity?: number;
  height?: number;
  width?: number;
  reveal?: number;
}

export interface DomTranslateOrigin
{
  x: number;
  y: number;
}

const NO_ORIGIN: DomTranslateOrigin = { x: 0, y: 0 };
const DOM_FINISH_GRACE_MS = 50;

const EASING_BEZIER: Record<Easing, [ number, number, number, number ] | null> = {
  [Easing.Linear]: null,
  [Easing.EaseInQuad]: [ 1 / 3, 0, 2 / 3, 1 / 3 ],
  [Easing.EaseOutQuad]: [ 1 / 3, 2 / 3, 2 / 3, 1 ],
  [Easing.EaseInOutQuad]: [ 0.455, 0.03, 0.515, 0.955 ],
  [Easing.EaseInPow]: [ 0.55, 0.085, 0.68, 0.53 ],
};

function sampleLinearEasing( f: ( t: number ) => number, samples: number = 24 ): string
{
  const parts: string[] = [];
  for ( let i = 0; i <= samples; i++ )
  {
    const t = i / samples;
    parts.push( `${f( t ).toFixed( 4 )} ${( t * 100 ).toFixed( 2 )}%` );
  }
  return `linear(${parts.join( ", " )})`;
}

export function cssEasing( easing: Easing, pow: number = 1, reversed: boolean = false ): string
{
  if ( easing === Easing.Linear )
    return "linear";

  if ( DOM_LINEAR_EASING_SUPPORTED )
  {
    const f = ( t: number ) => TransformBase.applyEasing( easing, t, pow );
    return sampleLinearEasing( reversed ? t => 1 - f( 1 - t ) : f );
  }

  const b = EASING_BEZIER[easing];
  if ( !b )
    return "linear";
  const [ x1, y1, x2, y2 ] = reversed ? [ 1 - b[2], 1 - b[3], 1 - b[0], 1 - b[1] ] : b;
  return `cubic-bezier(${x1}, ${y1}, ${x2}, ${y2})`;
}

function easingFor( tr: TransformBase, reversed: boolean ): string
{
  return tr.domEasing() ?? cssEasing( tr.getEasing(), tr.getEasingPow(), reversed );
}

function propertyFor( tr: TransformBase ): DomAnimatedProperty | null
{
  if ( tr instanceof TransformStraightMove ) return "translate";
  if ( tr instanceof TransformScale ) return "scale";
  if ( tr instanceof TransformOpacity ) return "opacity";
  if ( tr instanceof TransformRotate ) return "rotate";
  return null;
}

function targetRotates( tr: TransformBase ): boolean
{
  // Duck-typed: UIElement imports this module, so it cannot be imported back here.
  const target = tr.getAnimatedTarget() as { isRotatable?: () => boolean } | null;
  return target?.isRotatable?.() ?? true;
}

function translateCss( v: Vector2, origin: DomTranslateOrigin ): string
{
  return `${v.x + origin.x}px ${v.y + origin.y}px`;
}

function formatValue( prop: DomAnimatedProperty, tr: TransformBase, which: "from" | "to", origin: DomTranslateOrigin ): string | number
{
  switch ( prop )
  {
    case "translate":
    {
      const v = which === "from" ? ( tr as TransformStraightMove<never> ).startValue() : ( tr as TransformStraightMove<never> ).destValue();
      return translateCss( v, tr.isImportant() ? origin : NO_ORIGIN );
    }
    case "scale":
    {
      const v = which === "from" ? ( tr as TransformScale<never> ).startValue() : ( tr as TransformScale<never> ).destValue();
      return domScaleCss( v.x, v.y );
    }
    case "rotate":
    {
      const v = which === "from" ? ( tr as TransformRotate<never> ).startValue() : ( tr as TransformRotate<never> ).destValue();
      return `${v}rad`;
    }
    case "opacity":
    {
      const v = which === "from" ? ( tr as TransformOpacity<never> ).startValue() : ( tr as TransformOpacity<never> ).destValue();
      return v;
    }
  }
}

export function domPredecessorDoneBy( predecessor: TransformBase, at: number ): boolean
{
  return predecessor.calcProgress( at ) >= predecessor.getEarlyFinishRatio();
}

function currentValue( node: HTMLElement, prop: DomAnimatedProperty, tr: TransformBase, origin: DomTranslateOrigin ): string | number | null
{
  const target = ( tr as unknown as { getTarget(): Transformable | null } ).getTarget();
  if ( !target )
    return null;

  const predecessor = target.domImportantPredecessor( tr );
  if ( predecessor )
  {
    if ( domPredecessorDoneBy( predecessor, tr.startTime() ) )
      return formatValue( prop, predecessor, "to", origin );
    const live = getComputedStyle( node )[prop];
    if ( live )
      return prop === "opacity" ? Number( live ) : live;
  }

  switch ( prop )
  {
    case "translate": return translateCss( target.pixelPosImportant(), origin );
    case "scale":     { const v = target.getScaleImportant(); return domScaleCss( v.x, v.y ); }
    case "rotate":    return `${target.getRotationImportant()}rad`;
    case "opacity":   return target.getOpacityImportant();
  }
}

function identityValue( prop: DomAnimatedProperty ): string | number
{
  switch ( prop )
  {
    case "translate": return "0px 0px";
    case "scale":     return "1 1";
    case "rotate":    return "0rad";
    case "opacity":   return 1;
  }
}

export function sampleToKeyframe( s: DomSample, offset: number, origin: DomTranslateOrigin = NO_ORIGIN ): Keyframe
{
  const k: Keyframe = { offset, easing: "linear" };
  if ( s.pos ) k.translate = translateCss( s.pos, origin );
  if ( s.scale ) k.scale = domScaleCss( s.scale.x, s.scale.y );
  if ( s.rotation !== undefined ) k.rotate = `${s.rotation}rad`;
  if ( s.opacity !== undefined ) k.opacity = s.opacity;
  if ( s.height !== undefined ) k.height = `${s.height}px`;
  if ( s.width !== undefined ) k.width = `${s.width}px`;
  if ( s.reveal !== undefined ) k.clipPath = `inset(${( 1 - Math.max( 0, Math.min( 1, s.reveal ) ) ) * 100}% 0 0 0)`;
  return k;
}

function sampleCount( duration_ms: number ): number
{
  return Math.max( 8, Math.min( 120, Math.round( duration_ms / 16 ) ) );
}

function runAnimation( node: HTMLElement, keyframes: Keyframe[], options: KeyframeAnimationOptions ): Animation | null
{
  if ( !DOM_ANIMATIONS_SUPPORTED ) return null;
  try
  {
    const anim = node.animate( keyframes, options );
    const frame_time = document.timeline?.currentTime;
    if ( typeof frame_time === "number" )
      anim.startTime = frame_time;
    return anim;
  }
  catch ( e )
  {
    console.warn( "[dom_animations] animate() failed", e );
    return null;
  }
}

export interface DomSampledOptions
{
  delay?: number;
  iterations?: number;
  composite?: CompositeOperation;
  samples?: number;
  cancel_on_finish?: boolean;
}

export function startDomSampledAnimation(
  node: HTMLElement,
  duration_ms: number,
  sample: ( t: number ) => DomSample,
  options: DomSampledOptions = {}
): Animation | null
{
  const n = options.samples ?? sampleCount( duration_ms );
  const keyframes: Keyframe[] = [];
  for ( let i = 0; i <= n; i++ )
    keyframes.push( sampleToKeyframe( sample( i / n ), i / n ) );

  const anim = runAnimation( node, keyframes, {
    delay: Math.max( 0, options.delay ?? 0 ),
    duration: Math.max( 0, duration_ms ),
    fill: "forwards",
    iterations: options.iterations ?? 1,
    composite: options.composite ?? "replace",
  } );
  if ( anim && options.cancel_on_finish !== false && ( options.iterations ?? 1 ) !== Infinity )
    anim.finished.then( () => anim.cancel() ).catch( () => {} );
  return anim;
}

export function startDomDrift(
  node: HTMLElement,
  x: number,
  y: number,
  w: number,
  wrap_w: number,
  speed_px_per_s: number
): Animation | null
{
  if ( speed_px_per_s <= 0 ) return null;
  const to_edge_ms = Math.max( 0, ( wrap_w - x ) / speed_px_per_s * 1000 );
  const loop_ms = ( wrap_w + w ) / speed_px_per_s * 1000;
  const loop = (): Animation | null => runAnimation( node,
    [ { translate: `${-w}px ${y}px`, easing: "linear" }, { translate: `${wrap_w}px ${y}px` } ],
    { duration: loop_ms, iterations: Infinity } );

  if ( to_edge_ms < 16 )
    return loop();

  const first = runAnimation( node,
    [ { translate: `${x}px ${y}px`, easing: "linear" }, { translate: `${wrap_w}px ${y}px` } ],
    { duration: to_edge_ms, fill: "forwards" } );
  if ( !first ) return null;
  first.finished.then( () =>
  {
    first.cancel();
    const next = loop();
    if ( next ) ( first as unknown as { dom_next: Animation } ).dom_next = next;
  } ).catch( () => {} );
  return first;
}

export function cancelDomAnimation( anim: Animation | null | undefined ): void
{
  if ( !anim ) return;
  anim.cancel();
  const next = ( anim as unknown as { dom_next?: Animation } ).dom_next;
  if ( next ) next.cancel();
}

const DOM_ORPHAN_CANCEL_MS = 1000;

function finishOnOwn( anim: Animation | null, tr: TransformBase ): Animation | null
{
  if ( anim && !tr.isInfiniteLoop() )
    anim.finished.then( () =>
    {
      setTimeout( () =>
      {
        if ( tr.dom_animation === anim )
        {
          anim.cancel();
          tr.dom_animation = null;
        }
      }, DOM_ORPHAN_CANCEL_MS );
    } ).catch( () => {} );
  return anim;
}

function remainingMs( anim: Animation ): number
{
  const end = anim.effect?.getComputedTiming().endTime;
  const current = anim.currentTime;
  if ( end === undefined || current === null )
    return Infinity;
  return Number( end ) - Number( current );
}

export function releaseDomAnimation( tr: TransformBase, let_finish: boolean ): void
{
  const anim = tr.dom_animation;
  if ( !anim )
    return;
  if ( let_finish && anim.playState === "running" && remainingMs( anim ) <= DOM_FINISH_GRACE_MS )
    return;
  anim.cancel();
  tr.dom_animation = null;
}

export function startDomAnimation( node: HTMLElement, tr: TransformBase, now: number, origin: DomTranslateOrigin = NO_ORIGIN ): Animation | null
{
  if ( !DOM_ANIMATIONS_SUPPORTED ) return null;

  const early = tr.getEarlyFinishRatio();
  const early_ratio = early > 0 && early < 1 ? early : 1;
  const duration = Math.max( 0, tr.getDuration() * early_ratio );
  const options: KeyframeAnimationOptions = {
    delay: Math.max( 0, tr.startTime() - now ),
    duration,
    fill: "forwards",
    iterations: tr.isInfiniteLoop() ? Infinity : 1,
    composite: tr.isImportant() ? "replace" : "add",
  };

  const sampled0 = tr.domSample( 0 );
  if ( sampled0 )
  {
    const sample_origin = tr.isImportant() ? origin : NO_ORIGIN;
    const n = sampleCount( duration );
    const keyframes: Keyframe[] = [];
    for ( let i = 0; i <= n; i++ )
    {
      const t = i / n;
      const progress = i === n ? 1 : t * early_ratio;
      const s = tr.domSample( tr.sampleProgress( progress ) ) ?? sampled0;
      keyframes.push( sampleToKeyframe( s, t, sample_origin ) );
    }
    return stampDomAnimationStart( finishOnOwn( runAnimation( node, keyframes, options ), tr ), now );
  }

  const prop = propertyFor( tr );
  if ( !prop ) return null;
  if ( !tr.isImportant() && prop === "opacity" ) return null;
  // Rendering ignores rotation until enableRotation(); animating CSS `rotate` anyway
  // spun the info-overlay arrows mid-flight and snapped them back to 0 at the end.
  if ( prop === "rotate" && !targetRotates( tr ) ) return null;

  const to = formatValue( prop, tr, "to", origin );
  const from = tr.hasExplicitFrom()
    ? formatValue( prop, tr, "from", origin )
    : ( tr.isImportant() ? currentValue( node, prop, tr, origin ) : identityValue( prop ) );
  if ( from === null ) return null;

  const easing = easingFor( tr, false );
  const keyframes: Keyframe[] = [];
  if ( tr.isPingPong() )
  {
    const peak = Math.min( 0.999, Math.max( 0.001, tr.getPingPongThreshold() / 2 ) );
    keyframes.push( { [prop]: from, offset: 0, easing } as Keyframe );
    keyframes.push( { [prop]: to, offset: peak, easing: easingFor( tr, true ) } as Keyframe );
    keyframes.push( { [prop]: from, offset: 1 } as Keyframe );
  }
  else
  {
    keyframes.push( { [prop]: from, offset: 0, easing } as Keyframe );
    keyframes.push( { [prop]: to, offset: 1 } as Keyframe );
  }

  return stampDomAnimationStart( finishOnOwn( runAnimation( node, keyframes, options ), tr ), now );
}
